<template>
  <div class="dark-theme-wrapper">
    <div
      class="container d-flex justify-content-center align-items-center h-100"
    >
      <div class="login-card fade-in">
        <h2 class="main-title text-center mb-4">
          {{ isResetMode ? "OBNOVA HESLA" : "PŘIHLÁŠENÍ" }}
        </h2>
        <div class="separator mx-auto mb-5"></div>

        <div v-if="isLoggedIn" class="text-center logged-in-state">
          <p class="mb-4 text-white">Právě jste přihlášen.</p>
          <button @click="handleLogout" class="btn btn-outline-danger w-100">
            Odhlásit se
          </button>
        </div>

        <form v-else-if="isResetMode" @submit.prevent="handleResetPassword">
          <p class="text-center text-muted mb-4">
            Zadejte svůj email. Pošleme vám odkaz pro vytvoření nového hesla.
          </p>
          <div class="mb-4">
            <label class="form-label">EMAIL</label>
            <input
              type="email"
              v-model="email"
              class="form-control dark-input"
              placeholder="name@example.com"
              required
            />
          </div>
          <button type="submit" class="btn-red-glow w-100 mb-3">
            ODESLAT EMAIL
          </button>
          <button
            type="button"
            class="btn btn-outline-secondary w-100"
            @click="toggleResetMode"
          >
            ZPĚT NA PŘIHLÁŠENÍ
          </button>
        </form>

        <form v-else @submit.prevent="handleLogin">
          <div class="mb-4">
            <label class="form-label">EMAIL</label>
            <input
              type="email"
              v-model="email"
              class="form-control dark-input"
              placeholder="name@example.com"
              required
            />
          </div>

          <div class="mb-2">
            <label class="form-label">HESLO</label>
            <div class="password-wrapper">
              <input
                :type="passwordVisible ? 'text' : 'password'"
                v-model="password"
                class="form-control dark-input with-icon"
                placeholder="••••••••"
                required
              />
              <span
                class="password-toggle-icon"
                @click="passwordVisible = !passwordVisible"
                role="button"
              >
                <svg
                  v-if="passwordVisible"
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                  <circle cx="12" cy="12" r="3"></circle>
                </svg>
                <svg
                  v-else
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <path
                    d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"
                  ></path>
                  <line x1="1" y1="1" x2="23" y2="23"></line>
                </svg>
              </span>
            </div>
          </div>

          <div class="text-end mb-4">
            <a href="#" @click.prevent="toggleResetMode" class="forgot-link">
              Zapomněli jste heslo?
            </a>
          </div>

          <button type="submit" class="btn-red-glow w-100">PŘIHLÁSIT SE</button>

          <div class="text-center mt-4">
            <p class="register-text">
              Ještě nemáte účet?
              <router-link to="/register" class="register-link">
                Registrujte se zde
              </router-link>
            </p>
          </div>
        </form>

        <transition name="fade">
          <div v-if="errorMessage" class="alert-box error mt-4">
            ⚠️ {{ errorMessage }}

            <div
              v-if="showResendEmailBtn"
              class="mt-3 pt-2 border-top border-danger"
            >
              <p class="small mb-2 text-white-50">Nemůžete najít email?</p>
              <button
                @click="resendVerification"
                class="btn btn-sm btn-outline-light w-100"
                :disabled="resendLoading"
              >
                <span
                  v-if="resendLoading"
                  class="spinner-border spinner-border-sm me-2"
                ></span>
                Poslat ověřovací email znovu
              </button>
            </div>
          </div>
        </transition>

        <transition name="fade">
          <div v-if="successMessage" class="alert-box success mt-4">
            ✅ {{ successMessage }}
          </div>
        </transition>
      </div>
    </div>
  </div>
</template>

<script>
import { auth, db } from "../auth";
import {
  signInWithEmailAndPassword,
  signOut,
  sendPasswordResetEmail,
  sendEmailVerification,
} from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";

export default {
  name: "UserLogin",
  data() {
    return {
      email: "",
      password: "",
      passwordVisible: false,
      errorMessage: "",
      successMessage: "",
      isLoggedIn: !!auth.currentUser,
      isResetMode: false,

      showResendEmailBtn: false,
      unverifiedUser: null,
      resendLoading: false,
    };
  },
  methods: {
    toggleResetMode() {
      this.isResetMode = !this.isResetMode;
      this.errorMessage = "";
      this.successMessage = "";
      this.showResendEmailBtn = false;
    },

    async resendVerification() {
      auth.languageCode = "cs"; // Vynutíme češtinu

      if (!this.unverifiedUser) return;

      this.resendLoading = true;
      try {
        await sendEmailVerification(this.unverifiedUser);
        this.successMessage =
          "Ověřovací email byl odeslán! Zkontrolujte doručenou poštu i složku SPAM.";
        this.errorMessage = "";
        this.showResendEmailBtn = false;
      } catch (error) {
        console.error("Chyba při odesílání:", error);
        if (error.code === "auth/too-many-requests") {
          this.errorMessage =
            "Příliš mnoho pokusů. Počkejte chvíli a zkuste to znovu.";
        } else {
          this.errorMessage = "Chyba: " + error.message;
        }
      } finally {
        this.resendLoading = false;
      }
    },

    async handleResetPassword() {
      auth.languageCode = "cs";
      this.errorMessage = "";
      this.successMessage = "";

      if (!this.email) {
        this.errorMessage = "Zadejte prosím email.";
        return;
      }

      try {
        await sendPasswordResetEmail(auth, this.email);
        this.successMessage =
          "Email pro obnovu hesla byl odeslán! Zkontrolujte schránku.";
        setTimeout(() => {
          this.isResetMode = false;
          this.successMessage = "";
        }, 5000);
      } catch (error) {
        console.error(error);
        let msg = error.message;
        if (error.code === "auth/user-not-found")
          msg = "Tento email není registrován.";
        if (error.code === "auth/invalid-email")
          msg = "Neplatný formát emailu.";
        this.errorMessage = msg;
      }
    },

    async handleLogin() {
      this.errorMessage = "";
      this.successMessage = "";
      this.showResendEmailBtn = false;
      this.unverifiedUser = null;

      try {
        const userCredential = await signInWithEmailAndPassword(
          auth,
          this.email,
          this.password
        );
        const user = userCredential.user;

        // ======================================================
        // DŮLEŽITÉ: Obnovíme data ze serveru, abychom zjistili,
        // jestli uživatel mezitím neklikl na odkaz v emailu.
        // ======================================================
        await user.reload();

        // KONTROLA OVĚŘENÍ EMAILU
        if (!user.emailVerified) {
          this.unverifiedUser = user;
          this.showResendEmailBtn = true;
          throw new Error("unverified-email");
        }

        // KONTROLA DATABÁZE
        const userDoc = await getDoc(doc(db, "users", user.uid));

        if (userDoc.exists()) {
          const userData = userDoc.data();
          const role = userData.role || "user";

          this.successMessage = `Úspěšně přihlášen jako ${role}`;
          localStorage.setItem("userRole", role);
          this.isLoggedIn = true;

          setTimeout(() => {
            if (role === "admin") {
              this.$router.push("/admin");
            } else {
              this.$router.push("/account");
            }
          }, 1000);
        } else {
          throw new Error("Uživatel nenalezen v databázi");
        }
      } catch (error) {
        let msg = error.message;

        if (msg === "unverified-email") {
          msg =
            "Váš email ještě nebyl ověřen! Pro vstup musíte kliknout na odkaz, který jsme vám poslali.";
        } else {
          this.showResendEmailBtn = false;
          if (error.code === "auth/wrong-password") msg = "Nesprávné heslo.";
          if (error.code === "auth/user-not-found")
            msg = "Uživatel neexistuje.";
          if (error.code === "auth/invalid-email")
            msg = "Neplatný formát emailu.";
          if (error.code === "auth/too-many-requests")
            msg = "Příliš mnoho pokusů.";
          if (error.code === "auth/user-disabled") msg = "Účet zablokován.";
        }

        this.errorMessage = msg;
        this.successMessage = "";
      }
    },

    async handleLogout() {
      try {
        await signOut(auth);
        localStorage.removeItem("userRole");
        this.isLoggedIn = false;
        this.email = "";
        this.password = "";
        this.successMessage = "Byli jste odhlášeni.";
      } catch (error) {
        console.error("Chyba při odhlášení:", error);
      }
    },
  },
};
</script>

<style scoped>
/* Styly zůstávají stejné jako v předchozí verzi */
.dark-theme-wrapper {
  background-color: #000000;
  min-height: 100vh;
  color: #ffffff;
  font-family: "Roboto", sans-serif;
  display: flex;
  align-items: center;
  justify-content: center;
}
.login-card {
  background: #111;
  border: 1px solid #333;
  border-radius: 15px;
  padding: 3rem 2.5rem;
  width: 100%;
  max-width: 450px;
  box-shadow: 0 0 30px rgba(220, 53, 69, 0.15);
}
.main-title {
  font-size: 2rem;
  font-weight: 800;
  letter-spacing: 2px;
  color: #fff;
}
.separator {
  width: 60px;
  height: 4px;
  background-color: #dc3545;
  border-radius: 2px;
}
.form-label {
  font-size: 0.8rem;
  font-weight: bold;
  color: #888;
  letter-spacing: 1px;
  margin-bottom: 8px;
  display: block;
}
.dark-input {
  background-color: #000;
  border: 1px solid #444;
  color: #fff;
  padding: 12px 15px;
  border-radius: 8px;
  width: 100%;
  transition: all 0.3s ease;
}
.dark-input.with-icon {
  padding-right: 45px;
}
.dark-input:focus {
  border-color: #dc3545;
  outline: none;
  box-shadow: 0 0 0 3px rgba(220, 53, 69, 0.25);
}
.dark-input::placeholder {
  color: #444;
}
.password-wrapper {
  position: relative;
  width: 100%;
  display: block;
}
.password-toggle-icon {
  position: absolute;
  right: 15px;
  top: 50%;
  transform: translateY(-50%);
  color: #888;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.3s ease;
  z-index: 20;
}
.password-toggle-icon:hover {
  color: #dc3545;
}
.btn-red-glow {
  background: linear-gradient(135deg, #a71d2a 0%, #dc3545 100%);
  color: white;
  padding: 12px 20px;
  border: none;
  border-radius: 30px;
  font-weight: bold;
  text-transform: uppercase;
  letter-spacing: 1px;
  width: 100%;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(220, 53, 69, 0.4);
}
.btn-red-glow:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(220, 53, 69, 0.6);
}
.btn-outline-danger {
  background: transparent;
  border: 1px solid #dc3545;
  color: #dc3545;
  padding: 10px;
  border-radius: 30px;
  font-weight: bold;
  text-transform: uppercase;
  transition: all 0.3s ease;
}
.btn-outline-danger:hover {
  background: #dc3545;
  color: #fff;
}
.btn-outline-secondary {
  background: transparent;
  border: 1px solid #666;
  color: #888;
  padding: 10px;
  border-radius: 30px;
  font-weight: bold;
  text-transform: uppercase;
  transition: all 0.3s ease;
}
.btn-outline-secondary:hover {
  border-color: #888;
  color: #fff;
}
.register-text {
  color: #888;
  font-size: 0.9rem;
}
.register-link {
  color: #dc3545;
  font-weight: bold;
  text-decoration: none;
  transition: color 0.2s;
}
.register-link:hover {
  color: #ff6b6b;
  text-decoration: underline;
}
.forgot-link {
  color: #666;
  font-size: 0.85rem;
  text-decoration: none;
  transition: color 0.2s;
}
.forgot-link:hover {
  color: #dc3545;
}
.alert-box {
  padding: 12px;
  border-radius: 8px;
  font-size: 0.9rem;
  text-align: center;
  font-weight: bold;
}
.alert-box.error {
  background-color: rgba(220, 53, 69, 0.1);
  border: 1px solid #dc3545;
  color: #ff6b6b;
}
.alert-box.success {
  background-color: rgba(40, 167, 69, 0.1);
  border: 1px solid #28a745;
  color: #28a745;
}
.fade-in {
  animation: fadeIn 0.6s ease-out;
}
@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s;
}
.fade-enter,
.fade-leave-to {
  opacity: 0;
}
</style>
